"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { SanityMark } from "@/components/sanity-mark";

const PULL_START_SLOP = 12;
const PULL_THRESHOLD = 84;
const PULL_MAX = 128;
/** Degrees of mark rotation per px — scrubbed both ways with the finger. */
const SPIN_DEG_PER_PX = 5.5;
const MARK_PX = 22;

/** No-op: its presence lets iOS Safari set :active on touch. */
function enableActiveOnTouch() {}

/** Same targets as the global press in chrome/studio-press.css. */
const PRESS_TARGET =
  'button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"], .cursor-unified-tab';
/** Finger travel that turns a press into a pan. */
const PRESS_MOVE_CANCEL = 10;
/** A quick tap still shows the whole shrink before it springs back. */
const PRESS_MIN_MS = 90;
/** Sideways scrollers (workspace tabs, head tabs, chip rails): a touch there is
 * usually the start of a slide, so the press waits this long (or for the
 * finger to lift) instead of starting a scale tween on touch-down. */
const PRESS_SCROLLER_DELAY_MS = 100;
/** Finger travel that drops a press still waiting in a sideways scroller
 * (tighter than PRESS_MOVE_CANCEL: a slow slide must not flash a press). */
const PRESS_SCROLLER_SLOP = 6;
const PRESS_SCROLLER =
  ".cursor-unified-tabs-scroll, [data-h-scroll], .studio-admin-head-tabs, .cursor-h-scroll-fade";

/**
 * Touch / pen press: `data-pressing` on the touched button from pointerdown.
 * Mobile browsers only set :active once the tap is recognised (Chrome on
 * release, Safari needs a touch listener), so a back button that navigates
 * on that click never showed the press. The CSS treats `data-pressing` like
 * :active, and the exclusion list (cards, rows, menu items) still applies.
 *
 * Inside a sideways scroller the press is deferred (PRESS_SCROLLER_DELAY_MS,
 * or applied on lift for a quick tap): pressing on touch-down started a scale
 * tween under the finger as the slide began, and the tab bar visibly held the
 * tab before it scrolled (the old "transform tween holds the first pan").
 * Moving past PRESS_SCROLLER_SLOP or a pointercancel (the browser took the
 * pan) drops the pending press, so a slide never presses.
 */
function installTouchPress(): () => void {
  let pressed: Element | null = null;
  let pending: Element | null = null;
  let pendingTimer = 0;
  let downAt = 0;
  let startX = 0;
  let startY = 0;
  const timers = new Set<number>();
  // The credit ring presses with its button. Marking the ring here keeps
  // [data-pressing] out of a :has() in studio-press.css, which restyled the
  // whole document on every touch.
  const ringOf = (el: Element) =>
    el.parentElement?.classList.contains("studio-credit-balance-ring") ? el.parentElement : null;
  const unpress = (el: Element) => {
    el.removeAttribute("data-pressing");
    ringOf(el)?.removeAttribute("data-pressing");
  };
  const press = (el: Element) => {
    pressed = el;
    downAt = performance.now();
    el.setAttribute("data-pressing", "");
    if (!el.matches('[aria-disabled="true"]')) ringOf(el)?.setAttribute("data-pressing", "");
  };
  const dropPending = () => {
    if (pendingTimer) {
      window.clearTimeout(pendingTimer);
      timers.delete(pendingTimer);
      pendingTimer = 0;
    }
    pending = null;
  };
  const release = (immediate: boolean) => {
    const el = pressed;
    if (!el) return;
    pressed = null;
    const left = immediate ? 0 : PRESS_MIN_MS - (performance.now() - downAt);
    if (left <= 0) {
      unpress(el);
      return;
    }
    const id = window.setTimeout(() => {
      timers.delete(id);
      unpress(el);
    }, left);
    timers.add(id);
  };
  const onDown = (event: PointerEvent) => {
    if (event.pointerType === "mouse" || !event.isPrimary) return;
    release(true);
    dropPending();
    const target = event.target instanceof Element ? event.target.closest(PRESS_TARGET) : null;
    if (!target || target.matches(":disabled")) return;
    startX = event.clientX;
    startY = event.clientY;
    if (target.closest(PRESS_SCROLLER)) {
      pending = target;
      const id = window.setTimeout(() => {
        timers.delete(id);
        pendingTimer = 0;
        const el = pending;
        pending = null;
        if (el) press(el);
      }, PRESS_SCROLLER_DELAY_MS);
      pendingTimer = id;
      timers.add(id);
      return;
    }
    press(target);
  };
  const onMove = (event: PointerEvent) => {
    if ((!pressed && !pending) || !event.isPrimary) return;
    const moved = Math.hypot(event.clientX - startX, event.clientY - startY);
    if (pending && moved > PRESS_SCROLLER_SLOP) dropPending();
    if (pressed && moved > PRESS_MOVE_CANCEL) release(true);
  };
  const onUp = () => {
    // Quick tap in a sideways scroller: show the press now, for PRESS_MIN_MS.
    const el = pending;
    dropPending();
    if (el) press(el);
    release(false);
  };
  const onCancel = () => {
    dropPending();
    release(true);
  };
  const opts = { passive: true, capture: true } as const;
  document.addEventListener("pointerdown", onDown, opts);
  document.addEventListener("pointermove", onMove, opts);
  document.addEventListener("pointerup", onUp, opts);
  document.addEventListener("pointercancel", onCancel, opts);
  window.addEventListener("blur", onCancel);
  return () => {
    dropPending();
    release(true);
    timers.forEach((id) => window.clearTimeout(id));
    document.removeEventListener("pointerdown", onDown, opts);
    document.removeEventListener("pointermove", onMove, opts);
    document.removeEventListener("pointerup", onUp, opts);
    document.removeEventListener("pointercancel", onCancel, opts);
    window.removeEventListener("blur", onCancel);
  };
}

function insideZoomRegion(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest("[data-allow-zoom]"));
}

function insideDismissibleSheet(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(
      target.closest(
        ".studio-cn-book-sheet-panel, .studio-mobile-app-menu-sheet, .studio-history-mobile-sheet, .studio-action-menu-layer, .studio-action-menu",
      ),
    )
  );
}

function verticalPanBlocked(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.documentElement) {
    const action = getComputedStyle(el).touchAction;
    if (action.includes("none")) return true;
    if (action.includes("pan-x") && !action.includes("pan-y")) return true;
    el = el.parentElement;
  }
  return false;
}

/** No computed-style reads: any scrolled ancestor means the page is not at its top. */
function scrolledToTop(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.documentElement) {
    if (el.scrollTop > 0) return false;
    el = el.parentElement;
  }
  return true;
}

function canPullFrom(target: EventTarget | null): boolean {
  if ((target as Element | null)?.closest?.(".ads-root, .cm-page")) return false; // Ads and the community sign-up use the Ad Engine pull (AdsPullReload)
  return (
    !insideZoomRegion(target) &&
    !insideDismissibleSheet(target) &&
    scrolledToTop(target) &&
    !verticalPanBlocked(target)
  );
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

/**
 * Mobile gesture layer:
 * - Zoom guard via CSS touch-action + iOS gesture* (no always-on non-passive pan).
 * - Pull-to-reload: white circle + mark. Rotation is scrubbed to pull distance
 *   (down one way, back up reverses). Free-spins only after reload triggers.
 * - Every touch listener is passive, so the browser never waits on JS before a
 *   pan starts. html/body overscroll-behavior: none stops the native
 *   pull-to-refresh and edge gestures instead.
 */
export function MobileGestures() {
  const [pull, setPull] = useState(0);
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const unwindRafRef = useRef(0);
  const markRef = useRef<SVGSVGElement | null>(null);
  pullRef.current = pull;
  refreshingRef.current = refreshing;

  useEffect(() => installTouchPress(), []);

  useEffect(() => {
    const onGesture = (event: Event) => {
      event.preventDefault();
    };
    const blockPinch = (event: Event) => {
      if (event instanceof TouchEvent && event.touches.length > 1) event.preventDefault();
    };
    const opts = { passive: false, capture: true } as const;
    const passive = { passive: true, capture: true } as const;
    // A document-level non-passive touchmove makes every one-finger pan wait
    // for the main thread before scrolling. Only hold it while two fingers are down.
    let pinchArmed = false;
    const armPinch = (event: TouchEvent) => {
      if (pinchArmed || event.touches.length < 2) return;
      pinchArmed = true;
      document.addEventListener("touchmove", blockPinch, opts);
    };
    const disarmPinch = (event: TouchEvent) => {
      if (!pinchArmed || event.touches.length > 1) return;
      pinchArmed = false;
      document.removeEventListener("touchmove", blockPinch, opts);
    };
    document.addEventListener("gesturestart", onGesture, opts);
    document.addEventListener("gesturechange", onGesture, opts);
    document.addEventListener("gestureend", onGesture, opts);
    document.addEventListener("touchstart", armPinch, passive);
    // iOS Safari only applies :active on touch when a touchstart listener
    // exists; this no-op keeps the global button press (studio-press.css)
    // working even if the pinch guard above changes.
    document.addEventListener("touchstart", enableActiveOnTouch, passive);
    document.addEventListener("touchend", disarmPinch, passive);
    document.addEventListener("touchcancel", disarmPinch, passive);
    return () => {
      document.removeEventListener("gesturestart", onGesture, opts);
      document.removeEventListener("gesturechange", onGesture, opts);
      document.removeEventListener("gestureend", onGesture, opts);
      document.removeEventListener("touchstart", armPinch, passive);
      document.removeEventListener("touchstart", enableActiveOnTouch, passive);
      document.removeEventListener("touchend", disarmPinch, passive);
      document.removeEventListener("touchcancel", disarmPinch, passive);
      document.removeEventListener("touchmove", blockPinch, opts);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (unwindRafRef.current) cancelAnimationFrame(unwindRafRef.current);
    };
  }, []);

  // When free-spin starts, clear any leftover inline transform so CSS animation wins.
  useEffect(() => {
    if (!refreshing) return;
    const mark = markRef.current;
    if (mark) mark.style.transform = "";
  }, [refreshing]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(pointer: coarse)").matches) return;

    let startX = 0;
    let startY = 0;
    let startTarget: EventTarget | null = null;
    let decided = false;
    let dragging = false;

    const cancelUnwind = () => {
      if (unwindRafRef.current) {
        cancelAnimationFrame(unwindRafRef.current);
        unwindRafRef.current = 0;
      }
    };

    const setPullBoth = (px: number) => {
      pullRef.current = px;
      setPull(px);
    };

    const unwindPull = () => {
      cancelUnwind();
      const from = pullRef.current;
      if (from <= 0.5) {
        if (from !== 0) setPullBoth(0);
        setPulling(false);
        return;
      }
      const started = performance.now();
      const duration = 220;
      const tick = (now: number) => {
        const t = easeOutCubic(Math.min(1, (now - started) / duration));
        const next = from * (1 - t);
        setPullBoth(next);
        if (t < 1) {
          unwindRafRef.current = requestAnimationFrame(tick);
        } else {
          setPullBoth(0);
          setPulling(false);
          unwindRafRef.current = 0;
        }
      };
      unwindRafRef.current = requestAnimationFrame(tick);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (refreshingRef.current) return;
      // /ads scrolls inside .ads-root (window stays at 0): no pull-to-reload there.
      if (window.location.pathname.startsWith("/ads")) { decided = true; dragging = false; return; }
      decided = false;
      if (event.touches.length !== 1) {
        if (dragging) {
          dragging = false;
          unwindPull();
        }
        decided = true;
        return;
      }
      dragging = false;
      cancelUnwind();
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTarget = event.target;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (refreshingRef.current || event.touches.length !== 1) return;
      if (decided && !dragging) return;
      const touch = event.touches[0];
      const dy = touch.clientY - startY;
      if (!decided) {
        const dx = touch.clientX - startX;
        if (Math.abs(dy) < PULL_START_SLOP && Math.abs(dx) < PULL_START_SLOP) return;
        decided = true;
        // One check per gesture, and only for a downward drag.
        dragging = dy > 0 && Math.abs(dy) > Math.abs(dx) && canPullFrom(startTarget);
        if (!dragging) return;
        setPulling(true);
      }
      setPullBoth(Math.min(PULL_MAX, Math.max(0, (dy - PULL_START_SLOP) * 0.55)));
    };

    const onTouchEnd = () => {
      decided = false;
      if (!dragging) return;
      const distance = pullRef.current;
      dragging = false;
      setPulling(false);
      if (distance >= PULL_THRESHOLD) {
        cancelUnwind();
        refreshingRef.current = true;
        setRefreshing(true);
        setPullBoth(PULL_THRESHOLD);
        // Clear inline transform so CSS free-spin can run.
        if (markRef.current) markRef.current.style.transform = "";
        window.setTimeout(() => {
          try {
            window.dispatchEvent(new Event("studio:flush-tab-session"));
          } catch {
            /* ignore */
          }
          window.location.reload();
        }, 420);
      } else {
        unwindPull();
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      cancelUnwind();
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  const visible = pull > 0.5 || refreshing;
  if (!visible) return null;

  const progress = Math.min(1, pull / PULL_THRESHOLD);
  const spinDeg = pull * SPIN_DEG_PER_PX;
  const puckStyle = {
    transform: `translateY(${pull}px)`,
    opacity: refreshing ? 1 : Math.min(1, 0.4 + progress * 0.85),
  } as CSSProperties;

  // Scrub via React style while pulling. On refresh, only set the CSS var —
  // never an inline transform (that kills the free-spin animation).
  const markStyle = refreshing
    ? ({ ["--ys-ptr-spin" as string]: `${spinDeg}deg` } as CSSProperties)
    : ({ transform: `rotate(${spinDeg}deg)` } as CSSProperties);

  return (
    <div className="ys-ptr" aria-hidden="true">
      <div
        className={`ys-ptr-puck${pulling ? " is-pulling" : ""}${refreshing ? " is-refreshing" : ""}`}
        style={puckStyle}
      >
        <SanityMark ref={markRef} className="ys-ptr-mark" box={MARK_PX} style={markStyle} />
      </div>
    </div>
  );
}
